/**
 * ARCHITECTURE: Customer Portal v2 Checkout (Task 47 Phase 5)
 *
 * Two-column layout matching the v2 design: payment details on the left,
 * order summary on the right. All chrome via DaisyUI tokens (no inline
 * styles — fixes audit issue C-007). Stripe Elements mount and submission
 * behaviour is unchanged from v1; this is purely a chrome rewrite.
 *
 * The Header sits on top per Q4 (one shared header on every authenticated
 * page). Pay button copy is dynamic ("Pay £49.00 and activate") per the
 * migration map — the price comes from the resolved Plan.
 */
import { useEffect, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import type {
  Stripe,
  StripeElements,
  StripePaymentElement,
} from '@stripe/stripe-js';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../utils/api';
import type { Plan } from '../types';
import Header from '../components/Header';
import UserMenuDrawer from '../components/UserMenuDrawer';
import { LoadingSkeleton, ErrorState } from '@repro/ui';

function formatGbp(amount: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(amount);
}

export default function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const planId = searchParams.get('planId');
  const email = searchParams.get('email') || undefined;
  const orgId = searchParams.get('organisationId') || undefined;

  const { email: customerEmail, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [elements, setElements] = useState<StripeElements | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);
  const mountRef = useRef<HTMLDivElement>(null);
  const paymentElementRef = useRef<StripePaymentElement | null>(null);

  // -------------------------------------------------------------------------
  // Bootstrap: load Stripe + plan + subscription client secret in parallel
  // -------------------------------------------------------------------------

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!planId) {
          setError('Missing plan ID in checkout URL');
          return;
        }
        const [{ publishableKey }, plans] = await Promise.all([
          apiService.getStripeConfig(),
          apiService.getPlans().catch(() => [] as Plan[]),
        ]);

        if (!mounted) return;
        setPlan(plans.find((p) => p.id === planId) ?? null);

        const stripeInstance = await loadStripe(publishableKey);
        if (!mounted) return;
        setStripe(stripeInstance);

        const data = await apiService.createElementsSubscription({
          planId,
          customerEmail: email,
          organisationId: orgId,
        });
        if (!mounted) return;
        setClientSecret(data.clientSecret);
      } catch (err) {
        if (mounted) setError((err as Error).message);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [planId, email, orgId]);

  // -------------------------------------------------------------------------
  // Mount Stripe PaymentElement once Stripe + clientSecret are ready
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!stripe || !clientSecret || !mountRef.current) return;
    const els = stripe.elements({ clientSecret });
    setElements(els);
    const paymentElement = els.create('payment');
    paymentElementRef.current = paymentElement;
    paymentElement.on('ready', () => setReady(true));
    paymentElement.mount(mountRef.current);
    return () => {
      paymentElementRef.current?.unmount?.();
    };
  }, [stripe, clientSecret]);

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.origin + '/dashboard' },
      redirect: 'if_required',
    });
    if (confirmError) {
      setError(confirmError.message || 'Payment failed');
      setSubmitting(false);
      return;
    }
    window.location.href = '/dashboard';
  };

  const handleSignOut = () => {
    logout();
    navigate('/login');
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const userEmail = customerEmail ?? email ?? 'Account';
  const priceMonthly = plan?.price_monthly ?? 0;
  const payButtonLabel = priceMonthly
    ? `Pay ${formatGbp(priceMonthly)} and activate`
    : 'Activate subscription';

  return (
    <div className="min-h-screen bg-base-200">
      <Header userEmail={userEmail} onUserMenu={() => setDrawerOpen(true)} />

      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-base-content">Checkout</h1>
          <p className="text-sm text-base-content/60">
            Confirm your payment details to activate the subscription.
          </p>
        </div>

        {error && (
          <div className="mb-6">
            <ErrorState
              title="Checkout error"
              body={error}
              onRetry={() => navigate('/dashboard')}
            />
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
          {/* ----- Left column: payment form ----- */}
          <section
            aria-labelledby="payment-heading"
            className="flex flex-col gap-4 rounded-box border border-base-300 bg-base-100 p-6"
          >
            <h2
              id="payment-heading"
              className="text-base font-semibold text-base-content"
            >
              Payment details
            </h2>

            {!stripe || !clientSecret ? (
              <LoadingSkeleton rows={5} />
            ) : (
              <form
                onSubmit={handleSubmit}
                aria-label="Payment form"
                className="flex flex-col gap-4"
              >
                <div ref={mountRef} aria-label="Card details" />
                <button
                  type="submit"
                  disabled={!ready || submitting}
                  className="btn btn-primary mt-2 w-full"
                  aria-label={
                    submitting
                      ? 'Processing payment, please wait'
                      : 'Complete payment and activate subscription'
                  }
                >
                  {submitting ? (
                    <span className="loading loading-spinner loading-sm" />
                  ) : (
                    payButtonLabel
                  )}
                </button>
                <p className="text-center text-xs text-base-content/60">
                  Secure payment processed by Stripe. We never see your card
                  details.
                </p>
              </form>
            )}
          </section>

          {/* ----- Right column: order summary ----- */}
          <aside
            aria-labelledby="summary-heading"
            className="flex h-fit flex-col gap-4 rounded-box border border-base-300 bg-base-100 p-6"
          >
            <h2
              id="summary-heading"
              className="text-base font-semibold text-base-content"
            >
              Order summary
            </h2>

            {!plan ? (
              <LoadingSkeleton rows={3} />
            ) : (
              <>
                <div className="flex flex-col gap-1">
                  <p className="text-base font-semibold text-base-content">
                    {plan.name}
                  </p>
                  {plan.description && (
                    <p className="text-sm text-base-content/70">
                      {plan.description}
                    </p>
                  )}
                </div>

                <div className="h-px w-full bg-base-300" />

                <dl className="flex flex-col gap-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-base-content/70">Plan</dt>
                    <dd className="font-medium text-base-content">
                      {plan.name}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-base-content/70">Billing</dt>
                    <dd className="font-medium text-base-content">Monthly</dd>
                  </div>
                  {email && (
                    <div className="flex justify-between gap-2">
                      <dt className="text-base-content/70">Billing email</dt>
                      <dd className="truncate text-right font-medium text-base-content">
                        {email}
                      </dd>
                    </div>
                  )}
                </dl>

                <div className="h-px w-full bg-base-300" />

                <div className="flex justify-between text-base">
                  <span className="font-semibold text-base-content">
                    Total today
                  </span>
                  <span className="font-bold text-base-content">
                    {formatGbp(priceMonthly)}
                  </span>
                </div>
                <p className="text-xs text-base-content/60">
                  Renews monthly. Cancel any time from your dashboard.
                </p>
              </>
            )}
          </aside>
        </div>
      </main>

      <UserMenuDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        userEmail={userEmail}
        roleLabel="Customer"
        onSignOut={handleSignOut}
      />
    </div>
  );
}
